/** Client-side hints so Order Access history can show link names / Changed By
 *  when the backend still logs boolean true/false ORDER_STATUS rows, or omits
 *  a usable audit row entirely. */

const STORAGE_KEY = 'orderAccessHistoryHints.v1';
const MAX_HINTS = 200;
const MATCH_WINDOW_MS = 5 * 60 * 1000;
/** Backend timestamps can drift from the browser clock; allow a wider match for
 *  the known boolean ORDER_STATUS placeholder rows. */
const LOOSE_MATCH_WINDOW_MS = 24 * 60 * 60 * 1000;

const readHints = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const writeHints = (hints) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(hints.slice(0, MAX_HINTS)));
    } catch {
        /* ignore quota */
    }
};

export const currentOrderAccessUserLabel = () =>
    (localStorage.getItem('userEmail') || localStorage.getItem('email') || localStorage.getItem('userName') || '').trim();

const normalizeText = (value) => String(value ?? '').trim();

const isBoolish = (value) => {
    const v = normalizeText(value).toLowerCase();
    return v === 'true' || v === 'false' || v === '1' || v === '0';
};

/** Legacy backend placeholder: ORDER_STATUS with true/false (folder/file link flag). */
export const isLegacyOrderAccessBooleanEntry = (entry) => {
    if (entry?._orderAccessHint) return false;
    const type = String(entry?.actionType || '').trim().toUpperCase();
    if (type !== 'ORDER_STATUS') return false;
    return isBoolish(entry?.previousValue) || isBoolish(entry?.newValue);
};

const looksLikeOrderAccessBackendRow = (entry) => {
    const type = String(entry?.actionType || '').trim().toUpperCase();
    if (type === 'ORDER_ACCESS') return true;

    const detail = String(entry?.actionDetail || '').toLowerCase();
    if (/order\s*access|order\s*url|folder\s*url|file\s*url|url\s*name/.test(detail)) return true;

    if (isLegacyOrderAccessBooleanEntry(entry)) return true;

    return false;
};

const entryTimeMs = (iso) => {
    const t = new Date(iso || 0).getTime();
    return Number.isFinite(t) ? t : NaN;
};

const timeClose = (iso, clientTs, windowMs = MATCH_WINDOW_MS) => {
    const t = entryTimeMs(iso);
    if (!Number.isFinite(t) || !Number.isFinite(clientTs)) return false;
    return Math.abs(t - clientTs) <= windowMs;
};

const resolveChangedBy = (...candidates) => {
    for (const candidate of candidates) {
        const value = normalizeText(candidate);
        if (!value) continue;
        const lower = value.toLowerCase();
        if (lower === 'null' || lower === 'undefined' || lower === '—' || lower === '-') continue;
        return value;
    }
    return '';
};

/**
 * Record a local hint after a successful Order Access add/delete/update.
 * @param {{ ordernum: string, action: 'add'|'delete'|'update', urlName?: string, previousUrlName?: string, changedBy?: string, orderLineId?: string|number }} hint
 */
export const recordOrderAccessHistoryHint = (hint) => {
    const ordernum = String(hint?.ordernum || '').trim();
    if (!ordernum) return;

    const urlName = normalizeText(hint.urlName);
    const previousUrlName = normalizeText(hint.previousUrlName);
    if (!urlName && !previousUrlName) return;

    const entry = {
        ordernum,
        action: hint.action === 'delete' ? 'delete' : hint.action === 'update' ? 'update' : 'add',
        urlName,
        previousUrlName,
        changedBy: resolveChangedBy(hint.changedBy, currentOrderAccessUserLabel()),
        orderLineId: hint.orderLineId != null ? String(hint.orderLineId) : '',
        clientTs: Date.now(),
    };

    const next = [entry, ...readHints().filter((h) => h && h.ordernum)];
    writeHints(next);
};

const hintMatchesEntry = (hint, entry, windowMs = MATCH_WINDOW_MS) => {
    if (String(entry?.ordernum || '').trim() !== hint.ordernum) return false;
    if (!looksLikeOrderAccessBackendRow(entry) && entry?.actionType) return false;
    // Missing server timestamp: still allow match for legacy boolean placeholders.
    if (!entry?.changedAt && isLegacyOrderAccessBooleanEntry(entry)) return true;
    return timeClose(entry.changedAt, hint.clientTs, windowMs);
};

const enhanceEntryFromHint = (entry, hint) => {
    const next = { ...entry };
    const isDelete = hint.action === 'delete';
    const isUpdate = hint.action === 'update';
    const name = hint.urlName || hint.previousUrlName;

    next.actionType = 'ORDER_ACCESS';
    next.actionDetail = isDelete
        ? 'Order Access link removed'
        : isUpdate
            ? 'Order Access link updated'
            : 'Order Access link added';

    if (isDelete) {
        next.previousValue = hint.previousUrlName || hint.urlName || '';
        next.newValue = '';
    } else if (isUpdate) {
        next.previousValue = hint.previousUrlName || '';
        next.newValue = hint.urlName || name || '';
    } else {
        // Add: keep the prior link name (if any) so Previous Value shows what was there before.
        // Never keep backend boolean true/false placeholders.
        const prior = hint.previousUrlName || '';
        next.previousValue = isBoolish(prior) ? '' : prior;
        next.newValue = hint.urlName || name || '';
    }

    // Always prefer the user who performed the manual Order Access update.
    next.changedBy = resolveChangedBy(
        hint.changedBy,
        entry?.changedBy,
        entry?.lastEditedBy,
        entry?.user,
        currentOrderAccessUserLabel()
    );
    if (!next.ordernum) next.ordernum = hint.ordernum;

    next._orderAccessHint = {
        action: hint.action,
        urlName: hint.urlName || name,
        previousUrlName: hint.previousUrlName || next.previousValue || '',
    };

    return next;
};

const isOrderAccessHistoryEntry = (entry) =>
    String(entry?.actionType || '').trim().toUpperCase() === 'ORDER_ACCESS'
    || Boolean(entry?._orderAccessHint);

/**
 * For successive Order Access adds on the same order, fill blank Previous Value from
 * the prior Order Access entry's New Value (e.g. add "76451.1" then "GigaTest" →
 * the second row shows Previous = 76451.1, New = GigaTest).
 */
export const chainOrderAccessPreviousValues = (entries) => {
    if (!Array.isArray(entries) || entries.length === 0) return entries || [];

    const sortedAsc = [...entries].sort(
        (a, b) => new Date(a.changedAt || 0) - new Date(b.changedAt || 0)
    );

    const lastNewByOrder = new Map();
    const patched = new Map();

    sortedAsc.forEach((entry) => {
        if (!isOrderAccessHistoryEntry(entry)) return;

        const ordernum = String(entry?.ordernum || '').trim();
        if (!ordernum) return;

        const hint = entry._orderAccessHint;
        const action = hint?.action
            || (/removed|deleted/i.test(entry?.actionDetail || '') ? 'delete'
                : /updated/i.test(entry?.actionDetail || '') ? 'update'
                    : 'add');

        const currentNew = normalizeText(hint?.urlName || entry?.newValue);
        const currentPrev = normalizeText(hint?.previousUrlName || entry?.previousValue);
        const priorNew = lastNewByOrder.get(ordernum) || '';

        if ((action === 'add' || action === 'update') && !currentPrev && priorNew && !isBoolish(priorNew)) {
            patched.set(entry, {
                ...entry,
                previousValue: priorNew,
                _orderAccessHint: {
                    ...(hint || { action, urlName: currentNew }),
                    action: hint?.action || action,
                    urlName: hint?.urlName || currentNew,
                    previousUrlName: priorNew,
                },
            });
        }

        if (action === 'delete') {
            lastNewByOrder.set(ordernum, '');
        } else if (currentNew && !isBoolish(currentNew)) {
            lastNewByOrder.set(ordernum, currentNew);
        }
    });

    if (patched.size === 0) return entries;

    return entries.map((entry) => patched.get(entry) || entry);
};

/** Drop leftover true/false ORDER_STATUS rows once a real Order Access row exists nearby. */
const dropSupersededLegacyBooleanRows = (entries) => {
    const accessTimesByOrder = new Map();

    entries.forEach((entry) => {
        if (!isOrderAccessHistoryEntry(entry)) return;
        const ordernum = String(entry?.ordernum || '').trim();
        if (!ordernum) return;
        const t = entryTimeMs(entry.changedAt);
        if (!Number.isFinite(t)) return;
        const list = accessTimesByOrder.get(ordernum) || [];
        list.push(t);
        accessTimesByOrder.set(ordernum, list);
    });

    return entries.filter((entry) => {
        if (!isLegacyOrderAccessBooleanEntry(entry)) return true;
        const ordernum = String(entry?.ordernum || '').trim();
        const accessTimes = accessTimesByOrder.get(ordernum);
        if (!accessTimes || accessTimes.length === 0) return true;

        const entryTs = entryTimeMs(entry.changedAt);
        if (!Number.isFinite(entryTs)) {
            // No timestamp on the placeholder — safe to hide when this order has Order Access rows.
            return false;
        }
        return !accessTimes.some((t) => Math.abs(t - entryTs) <= LOOSE_MATCH_WINDOW_MS);
    });
};

/**
 * Merge matching hints into history entries, and inject synthetic ORDER_ACCESS
 * rows when the audit trail did not record a usable change.
 *
 * @param {object[]} entries
 * @param {{ ordernum?: string, ordernums?: string[] }} [options]
 *   Scope hints to one order (getOrderHistory) or a set of orders (all-history page).
 *   Without a scope, unused hints must NOT be injected — that previously polluted
 *   every order's unread badge with another order's Order Access changes.
 */
export const applyOrderAccessHistoryHints = (entries, options = {}) => {
    const list = Array.isArray(entries) ? [...entries] : [];
    const hints = readHints();

    if (hints.length === 0) {
        return chainOrderAccessPreviousValues(list);
    }

    const scopedOrders = new Set();
    const single = String(options?.ordernum || '').trim();
    if (single) scopedOrders.add(single);
    if (Array.isArray(options?.ordernums)) {
        options.ordernums.forEach((n) => {
            const key = String(n || '').trim();
            if (key) scopedOrders.add(key);
        });
    }
    const hasScope = scopedOrders.size > 0;

    const hintInScope = (hint) => {
        const ordernum = String(hint?.ordernum || '').trim();
        if (!ordernum) return false;
        if (hasScope) return scopedOrders.has(ordernum);
        return list.some((e) => String(e?.ordernum || '').trim() === ordernum);
    };

    const usedHints = new Set();
    const usedEntries = new Set();

    // Prefer closest-in-time matches so each hint upgrades the right boolean placeholder.
    const findBestHintForEntry = (entry, windowMs) => {
        let bestIdx = -1;
        let bestDelta = Infinity;
        const entryTs = entryTimeMs(entry.changedAt);

        hints.forEach((hint, hIdx) => {
            if (usedHints.has(hIdx)) return;
            if (!hintInScope(hint)) return;
            if (!hintMatchesEntry(hint, entry, windowMs)) return;

            const delta = Number.isFinite(entryTs)
                ? Math.abs(entryTs - hint.clientTs)
                : 0;
            if (delta < bestDelta) {
                bestDelta = delta;
                bestIdx = hIdx;
            }
        });

        return bestIdx;
    };

    const enhanced = list.map((entry, idx) => {
        let matchIdx = findBestHintForEntry(entry, MATCH_WINDOW_MS);
        if (matchIdx < 0 && isLegacyOrderAccessBooleanEntry(entry)) {
            matchIdx = findBestHintForEntry(entry, LOOSE_MATCH_WINDOW_MS);
        }
        if (matchIdx < 0) return entry;

        usedHints.add(matchIdx);
        usedEntries.add(idx);
        return enhanceEntryFromHint(entry, hints[matchIdx]);
    });

    // Second pass: unused hints can still claim leftover boolean rows for the same order.
    hints.forEach((hint, hIdx) => {
        if (usedHints.has(hIdx)) return;
        if (!hintInScope(hint)) return;

        let bestIdx = -1;
        let bestDelta = Infinity;
        enhanced.forEach((entry, eIdx) => {
            if (usedEntries.has(eIdx)) return;
            if (!isLegacyOrderAccessBooleanEntry(entry)) return;
            if (String(entry?.ordernum || '').trim() !== hint.ordernum) return;

            const entryTs = entryTimeMs(entry.changedAt);
            const delta = Number.isFinite(entryTs)
                ? Math.abs(entryTs - hint.clientTs)
                : 0;
            if (delta <= LOOSE_MATCH_WINDOW_MS && delta < bestDelta) {
                bestDelta = delta;
                bestIdx = eIdx;
            }
        });

        if (bestIdx < 0) return;
        usedHints.add(hIdx);
        usedEntries.add(bestIdx);
        enhanced[bestIdx] = enhanceEntryFromHint(enhanced[bestIdx], hint);
    });

    const injected = [];
    // Only inject synthetic rows when we know which order(s) this response is for.
    if (hasScope) {
        hints.forEach((hint, hIdx) => {
            if (usedHints.has(hIdx)) return;
            if (!hintInScope(hint)) return;
            if (!normalizeText(hint.urlName) && !normalizeText(hint.previousUrlName)) return;

            const isDelete = hint.action === 'delete';
            const isUpdate = hint.action === 'update';
            const name = hint.urlName || hint.previousUrlName;

            injected.push({
                id: `order-access-hint-${hint.clientTs}-${hint.ordernum}-${hIdx}`,
                ordernum: hint.ordernum,
                orderLineId: hint.orderLineId || null,
                actionType: 'ORDER_ACCESS',
                actionDetail: isDelete
                    ? 'Order Access link removed'
                    : isUpdate
                        ? 'Order Access link updated'
                        : 'Order Access link added',
                previousValue: isDelete
                    ? (hint.previousUrlName || hint.urlName || '')
                    : (hint.previousUrlName || ''),
                newValue: isDelete ? '' : (hint.urlName || name || ''),
                changedBy: resolveChangedBy(hint.changedBy, currentOrderAccessUserLabel()),
                changedAt: new Date(hint.clientTs).toISOString(),
                _orderAccessHint: {
                    action: hint.action,
                    urlName: hint.urlName || name,
                    previousUrlName: hint.previousUrlName || '',
                },
                _syntheticOrderAccessHint: true,
            });
        });
    }

    const merged = injected.length === 0
        ? enhanced
        : [...injected, ...enhanced].sort(
            (a, b) => new Date(b.changedAt || 0) - new Date(a.changedAt || 0)
        );

    return chainOrderAccessPreviousValues(dropSupersededLegacyBooleanRows(merged));
};
